import { PurchaseType, type PurchaseLicenseFor, type PurchaseLicenseModalData } from '~/types/licenses/licenses';
import { ContactType } from '~/types/person/contacts';
import { LICENSE_TIERS, isTopTierLicense, nextBusinessTierAbove, type LicenseInput } from '~/types/licenses/tiers';
import { resolveFeatureGate } from 'pb-billing-ui/feature-gates.ts';
import { ModalNames } from '~/types/modals';
import type { License } from '~/types/person/person';
import { checkoutAssignUrl, checkoutBusinessUrl, checkoutPurchaseUrl, checkoutUpgradeUrl } from '~/utils/cross-app-urls';
import { usePersonStore } from '~/stores/person';
import { useBusinessStore } from '~/stores/business';
import { UPGRADE_RETURN_PARAMS } from '~/composables/useUpgradeReturnNotice';

export const useLicensePurchase = () => {
    const authStore = useAuthStore();
    const personStore = usePersonStore();
    const { showWarning } = useGlobalToast();
    const route = useRoute();

    // The hive path checkout should return to. Strips the one-shot params that a page acts on
    // when it sees them: the triggers that auto-open this purchase flow (?upgrade / ?purchase /
    // ?subscribe), and every return-leg hint checkout writes on the way back — the set
    // useUpgradeReturnNotice reads and strips, not a copy of it, so the two cannot drift. Either
    // kind left on the return path fires a second time the moment the member lands — checkout
    // bounces them straight back in, or a stale outcome rides into the NEXT return, where the
    // notice reads the first value of each repeated key and confirms against the wrong licence.
    // Centralised here so every entry point is covered without per-caller cleanup.
    const ONE_SHOT_PARAMS = new Set<string>(['upgrade', 'purchase', 'subscribe', ...UPGRADE_RETURN_PARAMS]);
    const buildReturnTo = (): string => {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(route.query)) {
            if (ONE_SHOT_PARAMS.has(key) || value == null) continue;
            if (Array.isArray(value)) {
                value.forEach((v) => v != null && params.append(key, String(v)));
            } else {
                params.append(key, String(value));
            }
        }
        const query = params.toString();
        return query ? `${route.path}?${query}` : route.path;
    };

    // A license the loaded person owns that is neither assigned nor expired: giving one of these
    // away costs nothing, so it is the line between assigning a license and buying one.
    const hasSpareLicense = computed(() =>
        (personStore.person?.licenses ?? []).some((l) => !l.assigned_to && !l.expired),
    );

    // The company the viewer is acting FOR, when they are acting as the employer rather than as
    // themselves: an admin of this company, looking at one of this company's own employees. Null
    // in a member's own session, null for Privacy Bee staff (a support session keeps the surfaces
    // it has today), and null when the company the viewer acts for is not the company that employs
    // the person on screen — no company is ever offered a charge for someone it does not employ.
    //
    // Deliberately narrower than useEmployeePersonalTabsHidden's rule, which asks only whether an
    // admin is impersonating an employee. That one decides what to HIDE, where a wrong answer
    // costs a hidden tab. This one decides WHOSE CARD a page is about to charge, so it also has to
    // name the payer, and it returns the company rather than a boolean so no caller looks it up a
    // second time and gets a different answer.
    const employerContext = computed(() => {
        if (authStore.user?.identity?.is_pb) return null;
        if (!personStore.impersonating) return null;
        const employs = personStore.person?.employer;
        const actsFor = authStore.user?.employer;
        if (!employs || !actsFor || actsFor.id !== employs.id) return null;
        return { id: employs.id, name: employs.name, slug: actsFor.slug };
    });

    // A channel-partner company has no pool to buy from: its licenses are provisioned on demand when
    // an admin assigns one, which is why EmployeeDetailsDrawer drops its out-of-stock and purchase
    // affordances for such a company and never blocks an assignment. Sending its admin to the
    // business checkout offers a paid order for something that is not sold there.
    //
    // Read off `/me` — `employer.partner`, which UserResource serves straight from the company's
    // `is_partner` — and deliberately NOT off `businessStore.business?.is_partner`, which is the
    // same fact from the wrong place: that company is fetched by the business middleware, so on the
    // personal surfaces this composable runs on it is null and the question would quietly answer
    // "not a partner" for every company.
    const employerProvisionsOnDemand = computed(() => authStore.user?.employer?.partner === true);

    // May this actor start a purchase at all. ONE question, asked of whoever is buying, and read
    // both by the surfaces that decide whether to OFFER a purchase (dashboard.vue, data-brokers,
    // BillingTab, the assignment picker) and by the guard below that decides whether to start one.
    // The two have to be the same answer: a page that hides the control on the employee's flag
    // while the guard allows the company's purchase makes the business checkout unreachable from
    // that page, which is the shape of the bug this composable keeps producing.
    //
    // - Acting for the company → `write financial` on it, the gate question every business purchase
    //   surface already asks (employees/index.vue, CreateEmployeeModal, UploadEmployeesModal,
    //   EmployeeDetailsDrawer) and the one the business checkout re-checks server-side before it
    //   prices or charges anything. `write employees` alone is enough to impersonate an employee, so
    //   an employee-only admin reaches every purchase surface here; spending is a separate grant,
    //   and a READ grant on financial is not one — app-api#2114 settled exactly that for a
    //   proposal's approval link, where handing the link back on `read financial` was handing out
    //   spending authority on the company's primary card.
    // - Acting for themselves → `can_purchase_pro`, the employee self-purchase flag on their own
    //   employment record. That is the wrong record to read for a buyer who IS the company: it
    //   refuses a company purchase the company never disabled, and it lets an admin with no
    //   spending grant through on a flag about buying for themselves.
    const canPurchaseLicense = computed(() => {
        if (employerContext.value) {
            // Nothing is for sale to a partner company, whatever this admin's grants.
            if (employerProvisionsOnDemand.value) return false;
            // The business store is asked only in an employer context: this composable runs on
            // purely personal surfaces too (the dashboard, data brokers, a member's own billing
            // tab), and a company's permission list has no bearing on a member buying for
            // themselves.
            return useBusinessStore().hasPermission('financial', 'write');
        }
        const employer = authStore.user?.employer;
        if (!employer) return true;
        return employer.can_purchase_pro === true;
    });

    // What a surface should say INSTEAD of the purchase control it has just hidden, when the actor
    // is an admin acting for the company rather than an employee. `undefined` for a member, so
    // EmployeePurchaseGate keeps its own default copy, which is addressed to an employee and is
    // right for them.
    //
    // It exists because the refusal and the hidden control are read by different people. Hiding
    // the control takes the press-time explanation away with it, and the gate left in its place
    // says "Ask your company admin to assign you a license" — wrong about who an admin is, and
    // silent about the one thing they can actually do. The company's purchasing was never switched
    // off; their own grant was.
    const employerGateMessage = computed(() => {
        const company = employerContext.value;
        if (!company) return undefined;
        if (employerProvisionsOnDemand.value) {
            return `${company.name} provisions licenses when you assign them. Assign one from the employee's record in the business portal — there is nothing to buy first.`;
        }
        return `You don't have permission to buy licenses for ${company.name}. Ask an administrator with billing access.`;
    });

    // The same question at the moment of the press, with the refusal said out loud. Every purchase
    // entry point routes through it, including the ones that build their own checkout redirect and
    // therefore can't go through openPurchaseModal. A refusal names its own remedy, so a gated
    // actor gets feedback on every purchase surface instead of a dead no-op button — and the
    // remedy differs by actor: an employee asks their company, an admin asks for billing access.
    const ensureCanPurchase = (): boolean => {
        if (canPurchaseLicense.value) return true;
        showWarning(
            'Purchase Not Available',
            // The same words the hidden-control gate shows, by construction rather than by two
            // strings kept in step: an employer reads why their company cannot buy, a member reads
            // that theirs switched employee purchases off.
            employerGateMessage.value
                ?? 'Your employer has disabled license purchases for employees. Please contact your company administrator and request a license.'
        );
        return false;
    };

    // A company license is assigned to an EMPLOYEE, from the company roster. Two kinds of press
    // ask for something else, and a company purchase can reach neither, so both are refused rather
    // than charged: PB-013 settled that there is no family-specific company purchase — a family
    // license is the employee's own to buy, from their own session and on their own card.
    //
    // A NAMED RELATIVE. Read off the recipient, not off an id comparison: contacts and people are
    // different id spaces. Two readings are the employee themselves and a company purchase serves
    // both — `ContactType.PERSON`, which the locked-feature gates send (useLicenseAccess,
    // needs_gift), and the employee's OWN row in their family list, which useContactActions tags
    // `ContactType.CONTACT` like every other row and marks `is_self`. Only a CONTACT who is
    // someone else is a relative.
    //
    // THE FAMILY POOL, with nobody named yet: the family page and the billing tab buy an unassigned
    // license the holder hands on later. That intent arrives declared (`familyPool`) because it
    // cannot be inferred — `for: null` is a member's own pool or a company's, depending only on who
    // is buying, and a company really can buy unassigned licenses for its roster.
    //
    // Said out loud rather than hidden, like every other refusal here. Returns true when it
    // refused, so callers read `if (refuseEmployerFamilyPurchase(contact)) return;`.
    const refuseEmployerFamilyPurchase = (
        contact?: PurchaseLicenseFor | null,
        familyPool = false,
    ): boolean => {
        if (!employerContext.value) return false;
        const relative = contact?.type === ContactType.CONTACT && !contact.is_self;
        if (!relative && !familyPool) return false;
        showWarning(
            'A company license covers an employee',
            relative
                ? `A company license is assigned to an employee from the company roster, so it cannot be given to ${contact!.name}. A license for a family member is the employee's own to buy.`
                : "A company license is assigned to an employee from the company roster, so it cannot be bought here to hand on to a family member. A family license is the employee's own to buy."
        );
        return true;
    };

    // The one money path for a company's licenses (PB-013): the business checkout, which quotes
    // company plans and charges the company on a company invoice. Returns true when it fired, so
    // callers read `if (redirectEmployerPurchase()) return;`.
    //
    // Every consumer purchase surface quotes a personal plan and charges the signed-in admin's own
    // card. Reached from an employer context that is the page the business walkthrough found:
    // personal-looking, blended with the company, and nobody in the room able to say who pays.
    // Returning to the company's roster rather than to the employee's own page is part of the same
    // boundary — a company purchase begins and ends in the business portal, and the licenses it
    // buys are assigned from there afterwards.
    // The business twin of the consumer plan a locked feature needs. The two catalogues are one
    // ladder — LICENSE_TIERS ranks essentials/pro/signature 1/2/3 and their `_business`
    // counterparts identically — so the plan that carries a feature on one side names the plan
    // that carries it on the other. Without it an admin who followed a Signature-only feature
    // lands on the plan chooser, which leads with Pro, and buys a license that leaves the feature
    // locked.
    //
    // A hint only, like every other parameter on that URL: checkoutBusinessUrl documents that
    // checkout ignores a slug its own catalogue does not carry and re-quotes whatever the buyer
    // confirms. So an unpaired plan sends nothing and the buyer gets the chooser, as before.
    const businessPlanFor = (feature?: string): string | undefined => {
        const required = feature ? resolveFeatureGate(feature)?.requiredPlan : undefined;
        const paired = required ? `${required}_business` : '';
        return paired in LICENSE_TIERS ? paired : undefined;
    };

    const redirectEmployerPurchase = (feature?: string, upgradeFrom?: LicenseInput): boolean => {
        const company = employerContext.value;
        if (!company) return false;
        // Refused, and still `true`. `false` means "not an employer purchase" and falls through to
        // the consumer branches, which quote a personal plan and charge this admin's own card for
        // the company's protection — the exact defect this guard exists to stop. A company license
        // is still bought in the business checkout; by an admin who is allowed to spend.
        if (!ensureCanPurchase()) return true;
        // The plan the press was about: the company tier carrying the locked feature it came from,
        // or — for a bare "Upgrade", which names no feature — the next company tier above the license
        // on screen. Without either, checkout opens its chooser on every tier INCLUDING the one the
        // employee already holds, so an Upgrade press can buy a license that upgrades nothing. The
        // feature wins when both are known: it names the plan that actually unlocks what was asked
        // for, which may be more than one step up.
        window.location.href = checkoutBusinessUrl(
            company.id,
            company.slug ? `/business/${company.slug}/employees` : buildReturnTo(),
            { plan: businessPlanFor(feature) ?? nextBusinessTierAbove(upgradeFrom) },
        );
        return true;
    };

    // Redirect to checkout to buy a NEW license for the currently-loaded person. When viewing a
    // managed/impersonated person, pre-target the new license to them (checkoutAssignUrl →
    // assigned_to_id) so checkout can't default it to the authenticated buyer; plain self-purchase
    // otherwise. Raw redirect (no guard) shared by the purchase entry points below.
    const redirectNewPurchaseForCurrentPerson = (feature?: string) => {
        const beneficiaryId = personStore.impersonating;
        const target = personStore.person;
        if (beneficiaryId && target) {
            const name = `${target.first_name ?? ''} ${target.last_name ?? ''}`.trim();
            window.location.href = checkoutAssignUrl({ id: target.id, name }, buildReturnTo());
            return;
        }
        window.location.href = checkoutPurchaseUrl(buildReturnTo(), { feature });
    };

    // A license the loaded person owns as a person (owner.type === 1, owned by them) — the only
    // kind that can enter checkout's modify/upgrade flow. Employer/company-owned (owner.type !== 1)
    // or another person's license is managed elsewhere and 403s in checkout's change endpoints, so
    // it must never be sent there.
    const isModifiableByLoadedPerson = (license?: License | null): boolean => {
        if (!license) return false;
        return license.owner?.type === 1 && license.owner?.id === personStore.person?.id;
    };

    // Upgradeable = modifiable AND has a higher tier to move to (the top of each catalog is
    // excluded here). The billing "Modify License" flow instead allows any owned tier — a top-tier
    // license can still be downgraded / term-changed — so it gates on isModifiableByLoadedPerson.
    //
    // Returned as well as used here: every surface offering an upgrade asks this same question
    // about the loaded person's licence, and a surface answering it on its own is a second rule
    // that drifts from checkout's (which 403s a licence this one would have refused).
    const isUpgradeableLicense = (license?: License | null): boolean => {
        if (!license) return false;
        return !isTopTierLicense(license.slug) && isModifiableByLoadedPerson(license);
    };

    // Start a new-license purchase for the currently-loaded person, applying the employer guard.
    // Use this for entry points that buy "for whoever is on screen".
    const purchaseForCurrentPerson = () => {
        if (redirectEmployerPurchase()) return;
        if (!ensureCanPurchase()) return;
        redirectNewPurchaseForCurrentPerson();
    };

    const openPurchaseModal = (data: PurchaseLicenseModalData) => {
        // GIFT with an already-owned, unassigned license opens the in-hive assignment picker —
        // no new purchase and no Stripe redirect — so it runs BEFORE both guards below. A gated
        // employee (can_purchase_pro === false) can still assign a spare license they already own
        // to a family member, and an admin acting for the company is not sent to a checkout to do
        // something that costs nothing; only buying a NEW license is gated and routed below.
        if (data.type === PurchaseType.GIFT && data.contact) {
            if (hasSpareLicense.value) {
                useDynamicDialog(ModalNames.GIVE_LICENSE, { contact: data.contact, ...(data.feature ? { feature: data.feature } : {}) });
                return;
            }
        }

        // An admin acting for the company goes to the business checkout and nowhere else: every
        // branch below ends on a consumer purchase page, which would quote them a personal plan
        // and charge their own card for the company's protection. Placed here deliberately —
        // AFTER the branch above, because assigning a license that is already owned is not a
        // purchase. It carries the employer's own authorisation check (`write financial`) inside
        // it, so a company purchase is refused here rather than falling through to a consumer page
        // or landing on a business checkout that rejects the buyer on arrival.
        // A company purchase cannot reach an employee's family, named or not, so it is refused
        // before the redirect below rather than charged and left unfulfilled — the redirect carries
        // only the company and drops both the recipient and the family intent on the floor.
        if (refuseEmployerFamilyPurchase(data.contact, data.familyPool)) return;

        // `data.license` only when this press IS an upgrade: its presence is what tells the redirect
        // there is a tier to move up from, and a plain purchase has none.
        if (redirectEmployerPurchase(data.feature, data.type === PurchaseType.UPGRADE ? data.license : undefined)) return;

        if (!ensureCanPurchase()) return;

        // UPGRADE: send the license into checkout's modify flow only when it's actually modifiable
        // by the loaded person (owned + non-signature); otherwise a non-owned/gifted license id would
        // be pushed into modify. Fall back to a new purchase for the person on screen.
        if (data.type === PurchaseType.UPGRADE) {
            if (data.license?.id && isUpgradeableLicense(data.license)) {
                window.location.href = checkoutUpgradeUrl(data.license.id, buildReturnTo(), { feature: data.feature, plan: data.plan });
                return;
            }
            redirectNewPurchaseForCurrentPerson(data.feature);
            return;
        }

        // GIFT with no spare license: go straight to checkout pre-targeted to that contact so the
        // new license auto-assigns to them on purchase (the spare-license case is handled above,
        // before the purchase guard).
        if (data.type === PurchaseType.GIFT && data.contact) {
            window.location.href = checkoutAssignUrl({ id: data.contact.id, name: data.contact.name }, buildReturnTo());
            return;
        }

        // PURCHASE or unknown → checkout select-plan modal (skips the marketing pricing page).
        // When a caller targets a specific person (data.for) and we're viewing that person under
        // impersonation, pre-target the new license to them so it can't default to the buyer.
        // Callers with no target (e.g. buying unassigned managed licenses) stay a plain purchase.
        if (data.for && personStore.impersonating === data.for) {
            redirectNewPurchaseForCurrentPerson(data.feature);
            return;
        }
        window.location.href = checkoutPurchaseUrl(buildReturnTo(), { feature: data.feature });
    };

    return {
        buildReturnTo,
        hasSpareLicense,
        canPurchaseLicense,
        employerContext,
        employerGateMessage,
        ensureCanPurchase,
        isModifiableByLoadedPerson,
        redirectEmployerPurchase,
        isUpgradeableLicense,
        openPurchaseModal,
        refuseEmployerFamilyPurchase,
        purchaseForCurrentPerson,
    };
};
